'use client';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { translate, type Locale } from '@/lib/i18n';

type Ctx = {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (lt: string, vars?: Record<string, string | number>) => string;
};

const I18nCtx = createContext<Ctx>({
  locale: 'lt',
  setLocale: () => {},
  t: (lt) => lt,
});

export function I18nProvider({ initialLocale, children }: { initialLocale: Locale; children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      document.cookie = `lang=${l};path=/;max-age=31536000;samesite=lax`;
      localStorage.setItem('lang', l);
      document.documentElement.lang = l;
    } catch {
      /* nesvarbu */
    }
  }, []);

  const t = useCallback(
    (lt: string, vars?: Record<string, string | number>) => translate(locale, lt, vars),
    [locale],
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useT() {
  return useContext(I18nCtx);
}
